import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  TrendingUp,
  ShieldCheck,
  Landmark,
  PoundSterling,
  Loader2,
  Plus,
  Inbox,
  UserCheck,
  Check,
  X,
  Clock,
  XCircle,
  RefreshCw,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { company } from "../mock";

import { tr } from "../i18n";
const fmt = (n) => "£" + Math.round(n).toLocaleString("en-GB");
const pct = (n) => (n * 100).toFixed(2) + "%";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white ring-1 ring-[#0b1f3a]/8 p-6">
      <div className="w-11 h-11 rounded-full bg-[#f6f2e9] flex items-center justify-center mb-4">
        <Icon size={20} className="text-[#c2a15a]" />
      </div>
      <p className="text-[13px] text-[#0b1f3a]/55 uppercase tracking-wide">
        {label}
      </p>
      <p className="font-display text-[30px] text-[#0b1f3a] leading-none mt-1">
        {value}
      </p>
      {sub && <p className="text-[12px] text-[#0b1f3a]/45 mt-1.5">{sub}</p>}
    </div>
  );
}

const statusBadge = {
  pending: { label: "Pending", cls: "bg-[#f6f2e9] text-[#0b1f3a]/70" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-600" },
};

export default function Dashboard() {
  const { user, loading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [pf, setPf] = useState(null);
  const [leads, setLeads] = useState(null);
  const [regs, setRegs] = useState(null);
  const [err, setErr] = useState("");
  const [checking, setChecking] = useState(false);

  const isPartner = user?.role === "partner";
  const isApproved = isPartner || user?.status === "approved";

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const loadRegs = () =>
    api.get("/admin/registrations").then((r) => setRegs(r.data)).catch(() => {});

  useEffect(() => {
    if (!user) return;
    if (isPartner) {
      loadRegs();
      api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    } else if (isApproved) {
      api
        .get("/portfolio")
        .then((r) => setPf(r.data))
        .catch(() => setErr(tr("Could not load portfolio")));
    } else {
      // Pending/rejected client — re-check status once on load.
      refreshUser();
    }
  }, [user]);

  const decide = async (id, action) => {
    try {
      await api.post(`/admin/registrations/${id}/${action}`);
      loadRegs();
    } catch (_) {}
  };

  const recheckStatus = async () => {
    setChecking(true);
    await refreshUser();
    setChecking(false);
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#f6f2e9] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#c2a15a]" size={32} />
      </div>
    );
  }

  // ---- Client status page (pending / rejected) ----
  if (!isPartner && !isApproved) {
    const rejected = user.status === "rejected";
    return (
      <div className="min-h-screen bg-[#f6f2e9] flex flex-col">
        <header className="bg-[#0b1f3a]">
          <div className="max-w-6xl mx-auto px-5 md:px-8 h-[70px] flex items-center justify-between">
            <button onClick={() => navigate("/")} className="flex items-center gap-2">
              <span className="font-display text-[26px] text-white">
                {company.brandMark}
              </span>
              <span className="flex flex-col leading-none">
                <span className="text-[10px] tracking-[0.35em] text-[#c2a15a] font-medium">
                  {company.brandSub}
                </span>
                <span className="text-[9px] tracking-[0.2em] text-white/50">
                  {tr("PORTAL")}
                </span>
              </span>
            </button>
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="inline-flex items-center gap-2 text-white/70 hover:text-[#c2a15a] text-[13px] transition-colors"
            >
              <LogOut size={16} /> {tr("Sign out")}
            </button>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center px-5 py-16">
          <div className="max-w-lg w-full bg-white rounded-2xl ring-1 ring-[#0b1f3a]/8 p-10 text-center">
            <div
              className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${
                rejected ? "bg-red-50" : "bg-[#f6f2e9]"
              }`}
            >
              {rejected ? (
                <XCircle size={40} className="text-red-500" />
              ) : (
                <Clock size={40} className="text-[#c2a15a]" />
              )}
            </div>
            <h1 className="font-display text-[30px] text-[#0b1f3a]">
              {rejected ? tr("Application not approved") : tr("Your account is under review")}
            </h1>
            <p className="text-[#0b1f3a]/60 text-[16px] leading-relaxed mt-3">
              {rejected ? (
                <>
                  {tr("Thank you for your interest,")} {user.name.split(" ")[0]}{tr(". Unfortunately we are unable to open your account at this time. Please contact us if you have any questions.")}
                </>
              ) : (
                <>
                  {tr("Thank you for registering,")} {user.name.split(" ")[0]}{tr(". Our team at R&H is reviewing your application. You'll receive an email as soon as your account has been opened — then you can sign in to view your portfolio.")}
                </>
              )}
            </p>

            {!rejected && (
              <div className="mt-6 flex items-center justify-center gap-2 text-[13px]">
                <span className="inline-flex items-center gap-1.5 bg-[#f6f2e9] text-[#0b1f3a]/70 px-3 py-1.5 rounded-full uppercase tracking-wide">
                  <Clock size={13} /> {tr("Pending approval")}
                </span>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              {!rejected && (
                <button
                  onClick={recheckStatus}
                  disabled={checking}
                  className="inline-flex items-center justify-center gap-2 bg-[#0b1f3a] hover:bg-[#163561] disabled:opacity-60 text-white px-6 py-3 rounded-full text-[14px] font-medium transition-colors"
                >
                  {checking ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  {tr("Check status")}
                </button>
              )}
              <button
                onClick={() => navigate("/contact")}
                className="inline-flex items-center justify-center border border-[#0b1f3a]/15 hover:border-[#c2a15a] text-[#0b1f3a] px-6 py-3 rounded-full text-[14px] font-medium transition-colors"
              >
                {tr("Contact us")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const maxAmount = pf
    ? Math.max(...pf.holdings.map((h) => h.amount))
    : 1;

  const pendingCount = regs ? regs.filter((r) => r.status === "pending").length : 0;

  return (
    <div className="min-h-screen bg-[#f6f2e9]">
      {/* Top bar */}
      <header className="bg-[#0b1f3a]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-[70px] flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
          >
            <span className="font-display text-[26px] text-white">
              {company.brandMark}
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-[10px] tracking-[0.35em] text-[#c2a15a] font-medium">
                {company.brandSub}
              </span>
              <span className="text-[9px] tracking-[0.2em] text-white/50">
                {tr("PORTAL")}
              </span>
            </span>
          </button>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-white/60 text-[13px]">
              {user.name} ·{" "}
              <span className="text-[#c2a15a] capitalize">{user.role}</span>
            </span>
            <button
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="inline-flex items-center gap-2 text-white/70 hover:text-[#c2a15a] text-[13px] transition-colors"
            >
              <LogOut size={16} /> {tr("Sign out")}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-display text-[32px] md:text-[40px] text-[#0b1f3a]">
            {tr("Good day,")} {user.name.split(" ")[0]}
          </h1>
          <p className="text-[#0b1f3a]/55 mt-1">
            {isPartner
              ? tr("Review new registrations, approve accounts and manage enquiries.")
              : tr("Here's an overview of your cash across our partner institutions.")}
          </p>
        </div>

        {err && <p className="text-red-600 mb-4">{err}</p>}

        {/* Partner: registrations awaiting approval */}
        {isPartner && (
          <div className="rounded-2xl bg-white ring-1 ring-[#0b1f3a]/8 overflow-hidden mb-8">
            <div className="flex items-center gap-2 px-6 py-5 border-b border-[#0b1f3a]/8">
              <UserCheck size={18} className="text-[#c2a15a]" />
              <h2 className="font-display text-[22px] text-[#0b1f3a]">
                {tr("Account registrations")}
              </h2>
              {pendingCount > 0 && (
                <span className="ml-2 text-[12px] bg-[#c2a15a] text-[#0b1f3a] font-medium px-2.5 py-1 rounded-full">
                  {pendingCount} {tr("awaiting approval")}
                </span>
              )}
            </div>
            {!regs ? (
              <p className="px-6 py-8 text-[#0b1f3a]/50">{tr("Loading…")}</p>
            ) : regs.length === 0 ? (
              <p className="px-6 py-8 text-[#0b1f3a]/50">
                {tr("No registrations yet. New sign-ups from the website appear here for approval.")}
              </p>
            ) : (
              <div className="divide-y divide-[#0b1f3a]/6">
                {regs.map((r) => {
                  const b = statusBadge[r.status] || statusBadge.pending;
                  return (
                    <div
                      key={r.id}
                      className="px-6 py-4 flex flex-wrap items-center justify-between gap-3"
                    >
                      <div>
                        <p className="font-semibold text-[#0b1f3a] text-[15px]">
                          {r.name}{" "}
                          <span className="text-[12px] font-normal text-[#0b1f3a]/45">
                            · {r.email}
                          </span>
                        </p>
                        <span
                          className={`inline-flex items-center gap-1 mt-1 text-[11px] uppercase tracking-wide px-2.5 py-0.5 rounded-full ${b.cls}`}
                        >
                          {r.status === "pending" && <Clock size={11} />}
                          {b.label}
                        </span>
                      </div>
                      {r.status === "pending" ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => decide(r.id, "approve")}
                            className="inline-flex items-center gap-1.5 bg-[#0b1f3a] hover:bg-[#163561] text-white text-[13px] font-medium px-4 py-2 rounded-full transition-colors"
                          >
                            <Check size={15} /> {tr("Approve & open")}
                          </button>
                          <button
                            onClick={() => decide(r.id, "reject")}
                            className="inline-flex items-center gap-1.5 border border-[#0b1f3a]/15 hover:border-red-300 hover:text-red-600 text-[#0b1f3a]/70 text-[13px] font-medium px-4 py-2 rounded-full transition-colors"
                          >
                            <X size={15} /> {tr("Reject")}
                          </button>
                        </div>
                      ) : (
                        <span className="text-[12px] text-[#0b1f3a]/40">
                          {r.status === "approved" ? tr("Account opened") : tr("Declined")}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {pf && (
          <>
            {/* Stats */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
              <Stat
                icon={PoundSterling}
                label={tr("Total deposits")}
                value={fmt(pf.total)}
                sub={tr("across {v0} institutions", { v0: pf.institutions })}
              />
              <Stat
                icon={TrendingUp}
                label={tr("Blended rate")}
                value={pct(pf.blended_rate)}
                sub={tr("gross AER, weighted")}
              />
              <Stat
                icon={Landmark}
                label={tr("Est. annual interest")}
                value={fmt(pf.annual_interest)}
                sub={tr("projected over 12 months")}
              />
              <Stat
                icon={ShieldCheck}
                label={tr("FSCS covered")}
                value={fmt(pf.fscs_covered)}
                sub={tr("eligible deposits")}
              />
            </div>

            {/* Holdings */}
            <div className="rounded-2xl bg-white ring-1 ring-[#0b1f3a]/8 overflow-hidden mb-10">
              <div className="flex items-center justify-between px-6 py-5 border-b border-[#0b1f3a]/8">
                <h2 className="font-display text-[22px] text-[#0b1f3a]">
                  {tr("Your holdings")}
                </h2>
                <button className="inline-flex items-center gap-2 bg-[#0b1f3a] hover:bg-[#163561] text-white text-[13px] font-medium px-4 py-2.5 rounded-full transition-colors">
                  <Plus size={15} /> {tr("Place funds")}
                </button>
              </div>
              <div className="divide-y divide-[#0b1f3a]/6">
                {pf.holdings.map((h) => (
                  <div
                    key={h.id}
                    className="px-6 py-4 grid grid-cols-2 md:grid-cols-12 gap-3 items-center"
                  >
                    <div className="md:col-span-4">
                      <p className="font-semibold text-[#0b1f3a] text-[15px]">
                        {h.bank}
                      </p>
                      <p className="text-[13px] text-[#0b1f3a]/50">
                        {h.product}
                      </p>
                    </div>
                    <div className="md:col-span-4 order-last md:order-none col-span-2">
                      <div className="h-2 rounded-full bg-[#f6f2e9] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#c2a15a]"
                          style={{
                            width: `${(h.amount / maxAmount) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2 text-right md:text-left">
                      <p className="text-[13px] text-[#0b1f3a]/50">{tr("Rate")}</p>
                      <p className="font-medium text-[#0b1f3a]">{pct(h.rate)}</p>
                    </div>
                    <div className="md:col-span-2 text-right">
                      <p className="font-semibold text-[#0b1f3a]">
                        {fmt(h.amount)}
                      </p>
                      <p className="text-[12px] text-[#0b1f3a]/45">
                        {h.maturity}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Partner: leads inbox */}
        {user.role === "partner" && (
          <div className="rounded-2xl bg-white ring-1 ring-[#0b1f3a]/8 overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-5 border-b border-[#0b1f3a]/8">
              <Inbox size={18} className="text-[#c2a15a]" />
              <h2 className="font-display text-[22px] text-[#0b1f3a]">
                {tr("Enquiries & applications")}
              </h2>
              {leads && (
                <span className="ml-2 text-[12px] bg-[#f6f2e9] text-[#0b1f3a]/70 px-2.5 py-1 rounded-full">
                  {leads.length}
                </span>
              )}
            </div>
            {!leads ? (
              <p className="px-6 py-8 text-[#0b1f3a]/50">{tr("Loading…")}</p>
            ) : leads.length === 0 ? (
              <p className="px-6 py-8 text-[#0b1f3a]/50">
                {tr("No enquiries yet. Submissions from the website appear here.")}
              </p>
            ) : (
              <div className="divide-y divide-[#0b1f3a]/6">
                {leads.map((l) => (
                  <div
                    key={l.id}
                    className="px-6 py-4 flex flex-wrap items-center justify-between gap-3"
                  >
                    <div>
                      <p className="font-semibold text-[#0b1f3a] text-[15px]">
                        {l.name}{" "}
                        <span className="text-[12px] font-normal text-[#0b1f3a]/45">
                          · {l.email}
                        </span>
                      </p>
                      <p className="text-[13px] text-[#0b1f3a]/55">
                        {l.clientType} · {l.amount || "—"}
                      </p>
                    </div>
                    <span
                      className={`text-[11px] uppercase tracking-wide px-3 py-1 rounded-full ${
                        l.mode === "illustration"
                          ? "bg-[#f6f2e9] text-[#0b1f3a]/70"
                          : "bg-[#0b1f3a] text-white"
                      }`}
                    >
                      {l.mode === "illustration" ? tr("Illustration") : tr("Application")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
